// The certifications a recertification cycle template is for. A template with no types is available
// to every cycle; one with types only to cycles for a matching NREMT level, credential or license
// (type, and state when the license type lists states). The API decides what a cycle sees; these
// mirror its rules for labels and for the compliance form's template options.

import { RecertificationCycleTemplateTypes } from 'src/v3/core/types'

export const NREMT_TYPES = ['EMR', 'EMT', 'AEMT', 'Paramedic'] as const

/** The API's LicenseType values, as LicenseTypeDropdown offers them. */
export const LICENSE_TYPES = [
  'Advanced Care Paramedic (Canada Only)',
  'AEMT',
  'CFR',
  'EMR',
  'EMT',
  'EMT-1',
  'EMT-2',
  'EMT-B',
  'EMT-CC',
  'EMT-D',
  'Paramedic',
  'Primary Care Paramedic (Canada Only)',
  'Physician',
  'Physician Assistant',
  'Registered Nurse',
  'Other',
] as const

export type TemplateCycleType =
  | { kind: 'nremt' }
  | { kind: 'license'; type: string; state: string }
  | { kind: 'credential'; type: string }

/** The API lowercases some of these names, so compare without case. */
let same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase()

export let hasNoTypes = (types?: RecertificationCycleTemplateTypes) =>
  !types ||
  (types.nremt.length === 0 &&
    types.credentials.length === 0 &&
    types.licenses.length === 0)

let statesLabel = (states: string[]) =>
  states.length === 0
    ? 'Any state'
    : states.length <= 3
      ? states.join(', ')
      : `${states.length} states`

/** "NREMT Paramedic", "BLS", "EMT License · Ohio, Texas", as the mobile apps label them. */
export let templateTypeLabels = (types?: RecertificationCycleTemplateTypes) =>
  !types
    ? []
    : [
        ...types.nremt.map((t) => `NREMT ${t}`),
        ...types.credentials,
        ...types.licenses.map(
          (l) => `${l.type} License · ${statesLabel(l.states)}`,
        ),
      ]

/**
 * Whether a compliance recertification item of this cycle type can use the template (the API's
 * allowsComplianceItem). A compliance item for NREMT has no level, so any NREMT type matches it.
 */
export let templateAllowsCycleType = (
  types: RecertificationCycleTemplateTypes | undefined,
  cycleType: TemplateCycleType,
) => {
  if (!types || hasNoTypes(types)) return true
  if (cycleType.kind === 'nremt') return types.nremt.length > 0
  if (cycleType.kind === 'credential')
    return types.credentials.some((t) => same(t, cycleType.type))
  return types.licenses.some(
    (l) =>
      same(l.type, cycleType.type) &&
      (l.states.length === 0 || l.states.some((s) => same(s, cycleType.state))),
  )
}
